import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Trash, FileText, UploadSimple, CircleNotch, Download } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Contact KYC Documents — uploads attachments under document_type="contacts".
 * Backed by the existing GridFS-safe attachment system. Use for trade
 * licenses, VAT certificates, ID proofs, etc.
 */
export default function ContactDocuments({ contact, onChange }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/documents/attachments/contacts/${contact.id}`);
      setDocs(res.data?.attachments || res.data || []);
    } catch (e) {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (contact?.id) load(); }, [contact?.id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (20MB limit)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API_URL}/documents/attachments/contacts/${contact.id}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Uploaded ${file.name}`);
      e.target.value = '';
      load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/documents/attachments/${id}`);
      toast.success('Document deleted');
      load();
      onChange?.();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleDownload = (id, name) => {
    window.open(`${API_URL}/documents/attachments/download/${id}`, '_blank', 'noopener');
  };

  return (
    <div className="space-y-3" data-testid="contact-documents">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Upload Trade License, VAT Certificate, ID, etc. (20MB max per file)
        </p>
        <label className="inline-flex">
          <input
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            data-testid="contact-doc-upload-input"
          />
          <Button type="button" size="sm" disabled={uploading} asChild>
            <span className="cursor-pointer" data-testid="contact-doc-upload-btn">
              {uploading ? <CircleNotch className="mr-2 animate-spin" size={14} /> : <UploadSimple className="mr-2" size={14} />}
              Upload Document
            </span>
          </Button>
        </label>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground border-2 border-dashed rounded-lg">
          No KYC documents uploaded yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2"
              data-testid={`contact-doc-${d.id}`}
            >
              <FileText size={20} weight="duotone" className="text-blue-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.original_filename}</div>
                <div className="text-[11px] text-muted-foreground">
                  {(d.file_size / 1024).toFixed(1)} KB • {d.uploaded_by_name || '—'} •{' '}
                  {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}
                </div>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => handleDownload(d.id)} data-testid={`contact-doc-download-${d.id}`}>
                <Download size={16} />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(d.id)} data-testid={`contact-doc-delete-${d.id}`}>
                <Trash size={16} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
